/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Colors, ProgressBar, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { abbreviateNumber } from './abbreviateNumber';

export type Props = {
  /**
   * What the `UsageBar` is measuring.
   */
  label: string;
  /**
   * How much can be used.
   */
  max: number;
  /**
   * How much has been used.
   */
  value: number;
};

const Styles = labelStyles('UsageBar', {
  root: {
    display: 'block',
    paddingTop: '10px',
  },
  text: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: 'auto 1fr',
    marginBottom: 5,
  },
  label: {
    ...Typography.smallPrint1Semibold,
    color: Colors.neutral700,
  },
  amounts: {
    ...Typography.smallPrint1Medium,
    textAlign: 'right',
    color: Colors.neutral700,
  },
});

const formatNumber = (number: number): string => {
  const formatted =
    number > 9999 ? abbreviateNumber(number) : number.toLocaleString();
  return typeof formatted === 'string' ? formatted : 'Error';
};

/**
 * @private
 * Renders a `ProgressBar` for usage with labels.
 */
const UsageBar = (props: Props) => {
  const amount =
    props.max === Infinity
      ? 'Unlimited'
      : `${formatNumber(props.value)} / ${formatNumber(props.max)}`;
  return (
    <figure css={Styles.root} data-zds>
      <p css={Styles.text} data-zds>
        <span aria-hidden={true} css={Styles.label} data-zds>
          {props.label}
        </span>
        <span aria-hidden={true} css={Styles.amounts} data-zds>
          {amount}
        </span>
      </p>
      {props.max !== Infinity && (
        <ProgressBar max={props.max} value={props.value} />
      )}
    </figure>
  );
};

export default UsageBar;
